import React from 'react'
import type { JSX } from 'react'
import { cn } from '@/utilities/ui'
import { getSectionProps, getEffectiveBackground } from '@/utilities/sectionSettings'
import FadeIn from '@/components/ui/FadeIn'

type BackgroundShade = 'white' | 'neutral-50' | 'neutral-100'

type SectionSettings = {
  anchorId?: string | null
  backgroundShade?: BackgroundShade | null
  isDarkMode?: boolean | null
  padding?: 'md' | 'lg' | null
}

type SectionBackground = 'white' | 'light-gray' | 'brand' | 'dark' | 'transparent'

type SectionWrapperProps<T extends keyof JSX.IntrinsicElements = 'section'> = {
  as?: T
  settings?: SectionSettings
  background?: SectionBackground
  isDarkMode?: boolean
  className?: string
  children?: React.ReactNode
  id?: string
  container?: boolean
  fullWidth?: boolean
  padding?: 'none' | 'md' | 'lg'
  /** Animate content fade-in on scroll. Defaults to true. Set false for blocks with their own animations. */
  animate?: boolean
} & Omit<JSX.IntrinsicElements[T], 'className' | 'children' | 'id'>

const paddingClasses: Record<'none' | 'md' | 'lg', string> = {
  none: '',
  md: 'py-12 lg:py-24',
  lg: 'py-20 lg:py-32',
}

const topPaddingMap: Record<'md' | 'lg', string> = {
  md: 'pt-12 lg:pt-24',
  lg: 'pt-20 lg:pt-32',
}

// Normalize any legacy value (sm/xl from pre-migration data) into the current two-token system.
function normalizePadding(value: unknown): 'md' | 'lg' {
  if (value === 'lg' || value === 'xl') return 'lg'
  return 'md'
}

export function SectionWrapper<T extends keyof JSX.IntrinsicElements = 'section'>({
  as,
  settings,
  background,
  isDarkMode,
  className,
  children,
  id,
  container,
  fullWidth,
  padding,
  animate = true,
  ...rest
}: SectionWrapperProps<T>) {
  const Tag = (as || 'section') as any
  const base = getSectionProps(settings)

  const mappedBackground = getEffectiveBackground(settings)
  const bgToken: SectionBackground = background ?? mappedBackground ?? 'white'

  const backgroundClasses: Record<SectionBackground, string> = {
    white: 'bg-background',
    'light-gray': 'bg-light-gray',
    brand: 'bg-brand',
    dark: 'bg-dark',
    transparent: 'bg-transparent',
  }

  const paddingToken: 'none' | 'md' | 'lg' = (() => {
    if (padding !== undefined) return padding
    return normalizePadding(settings?.padding)
  })()

  const hasBottomPaddingOverride = className && /(?:^|\s)pb-/.test(className)
  const effectivePaddingClasses =
    hasBottomPaddingOverride && paddingToken !== 'none'
      ? topPaddingMap[paddingToken]
      : paddingClasses[paddingToken]

  const outerClassName = cn(
    backgroundClasses[bgToken],
    effectivePaddingClasses,
    className,
    'w-full',
  )

  const useFullWidth = fullWidth === true || container === false
  const containerClassName = useFullWidth ? 'w-full' : 'container mx-auto px-4 md:px-8'

  return (
    <Tag
      id={id || base.id}
      className={outerClassName}
      data-theme={(isDarkMode ?? base['data-theme'] === 'dark') ? 'dark' : undefined}
      {...rest}
    >
      {animate ? (
        <FadeIn className={containerClassName}>{children}</FadeIn>
      ) : (
        <div className={containerClassName}>{children}</div>
      )}
    </Tag>
  )
}
